import { useState, useEffect } from "react";
import { ExternalLink, Globe, Loader2, AlertCircle } from "lucide-react";
import { motion, type Variants } from "framer-motion";

interface OgMetadata {
  title?: string;
  description?: string;
  image?: { url: string };
  logo?: { url: string };
  publisher?: string;
  url: string;
}

interface UrlPreviewCardProps {
  url: string;
  compact?: boolean;
}

const UrlPreviewCard = ({ url, compact = false }: UrlPreviewCardProps) => {
  const [metadata, setMetadata] = useState<OgMetadata | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchMetadata = async () => {
      setLoading(true);
      setError(false);
      try {
        const response = await fetch(`https://api.microlink.io/?url=${encodeURIComponent(url)}`);
        const data = await response.json();
        
        if (isMounted) {
          if (data.status === "success") {
            setMetadata(data.data);
          } else {
            setError(true);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(true);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchMetadata();
    return () => { isMounted = false; };
  }, [url]);

  if (loading) {
    return (
      <div className={`flex items-center justify-center p-4 rounded-xl border border-border/40 bg-background/20 ${compact ? 'h-full' : 'h-32'}`}>
        <Loader2 className="w-5 h-5 animate-spin text-primary/60" />
      </div>
    );
  }

  if (error || !metadata) {
    return (
      <div className="flex items-center gap-3 p-3 rounded-xl border border-border/40 bg-background/20 group">
        <div className="p-2 rounded-lg bg-muted/50 text-muted-foreground group-hover:text-primary transition-colors">
          <Globe className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium truncate text-foreground group-hover:text-primary transition-colors">{url}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <AlertCircle className="w-3 h-3" />
            Preview unavailable
          </p>
        </div>
        <button
          onClick={(e) => {
            e.stopPropagation();
            window.open(url, "_blank", "noopener,noreferrer");
          }}
          className="p-1.5 rounded-full hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"
        >
          <ExternalLink className="w-4 h-4" />
        </button>
      </div>
    );
  }

  const { title, description, image, logo, publisher } = metadata;

  // Animation Variants
  const buttonVariants: Variants = {
    initial: { 
      scale: 0, 
      opacity: 0,
      transition: { 
        duration: 0.25, 
        ease: [0.4, 0, 1, 1] // Fast ease-out
      }
    },
    hover: { 
      scale: 1, 
      opacity: 1, 
      transition: { 
        duration: 0.25,
        // Sharp overshoot effect
        ease: [0.175, 0.885, 0.32, 1.4] 
      } 
    }
  };

  if (compact) {
    return (
      <motion.div 
        initial="initial"
        whileHover="hover"
        className="flex flex-col rounded-xl border border-primary/20 bg-primary/5 group overflow-hidden h-full relative"
      >
        {image?.url && (
          <div className="w-full h-1/2 shrink-0 overflow-hidden bg-muted">
            <img src={image.url} alt={title} className="w-full h-full object-cover" />
          </div>
        )}
        <div className="flex-1 min-w-0 p-2 flex flex-col justify-start gap-1 overflow-hidden">
          <div className="flex items-center gap-1">
            {logo?.url && <img src={logo.url} alt="" className="w-2.5 h-2.5 rounded-sm" />}
            <span className="text-[8px] text-primary/70 font-medium truncate uppercase tracking-tighter">{publisher || new URL(url).hostname}</span>
          </div>
          <p className="text-[10px] font-bold text-foreground line-clamp-2 group-hover:text-primary transition-colors leading-tight">
            {title || url}
          </p>
        </div>
        
        <motion.button
          variants={buttonVariants}
          onClick={(e) => {
            e.stopPropagation();
            window.open(url, "_blank", "noopener,noreferrer");
          }}
          className="absolute top-1 right-1 p-1.5 rounded-full bg-background shadow-md text-primary z-10 border border-primary/20 hover:scale-110 active:scale-90"
          title="Open link"
        >
          <ExternalLink className="w-3 h-3" />
        </motion.button>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial="initial"
      whileHover="hover"
      animate={{ opacity: 1, y: 0 }}
      className="mx-auto w-full max-w-md rounded-2xl border border-primary/20 bg-gradient-to-br from-background/40 to-primary/5 overflow-hidden group hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-shadow"
    >
      {image?.url && (
        <div className="w-full h-32 overflow-hidden bg-muted relative">
          <img src={image.url} alt={title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
        </div>
      )}
      <div className="p-4 flex flex-col gap-2 relative">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              {logo?.url && <img src={logo.url} alt="" className="w-4 h-4 rounded-sm" />}
              <span className="text-xs font-semibold text-primary/80 uppercase tracking-wider">{publisher || new URL(url).hostname}</span>
            </div>
            <h3 className="text-base font-bold text-foreground line-clamp-2 group-hover:text-primary transition-colors leading-snug">
              {title || url}
            </h3>
          </div>
          
          <motion.button
            variants={buttonVariants}
            onClick={(e) => {
              e.stopPropagation();
              window.open(url, "_blank", "noopener,noreferrer");
            }}
            className="p-2.5 rounded-full bg-background shadow-lg text-primary border border-primary/20 hover:scale-110 active:scale-90"
            title="Open link"
          >
            <ExternalLink className="w-4 h-4" />
          </motion.button>
        </div>
        
        {description && (
          <p className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
            {description}
          </p>
        )}
        
        <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground/60 group-hover:text-muted-foreground transition-colors">
          <Globe className="w-3 h-3" />
          <span className="truncate">{url}</span>
        </div>
      </div>
    </motion.div>
  );
};

export default UrlPreviewCard;
